import React, { forwardRef } from "react";
import { avatarToneFor } from "../../lib/avatar-tone.js";
import { SHAPE_CLASS } from "../../lib/shape.js";
import { TONES, type StatusTone } from "../../lib/tones.js";
import { cn } from "../../lib/utils.js";
import { ChipRemoveButton } from "../Chip/ChipRemoveButton.js";

/* ---------------- OwnerChip ----------------
   Tone colors the inner initials badge (a sub-element), so it stays a lookup
   map rather than CVA. With no new option set, the chip renders today's
   exact classes:
   - size="sm" steps the type down (text-dense-2 name, text-dense-3
     initials). The disc stays 20px and keeps its seed colour at every size.
   - embedded drops the pill's border, fill and padding inside a host row.
   - onRemove renders Chip's ChipRemoveButton after the name: Button ghost
     iconOnly xs circle on a 16px layout box, 44px under a coarse pointer.
   An embedded or removable chip fits its container: the name truncates
   first, and the disc and the remove control never shrink. The initials
   disc stays hand-built: Avatar has no 20px size (OK-65). */

/** The semantic tones an owner's disc takes (C7), as on Avatar. */
export type OwnerChipTone = Extract<StatusTone, "neutral" | "ai">;

/** md (default) is today's type; sm is the dense step. */
export type OwnerChipSize = "sm" | "md";

export interface OwnerChipProps extends React.HTMLAttributes<HTMLSpanElement> {
  /** Initials or a compact decorative icon for an unassigned owner. */
  initials: React.ReactNode;
  name?: React.ReactNode;
  /**
   * The disc's semantic tone (C7). Omitted (the default), the disc takes the
   * person's own gradient from `seed` (a user id) or, failing that, the text
   * of `name` and `initials`, so the same person is the same colour
   * everywhere (d36). `neutral` is the quiet elevated disc and `ai` is the
   * Player AI soft paint.
   */
  tone?: OwnerChipTone;
  seed?: string | null;
  /** md (default): today's 12px name. sm: a text-dense-2 name and
   *  text-dense-3 initials. The disc stays 20px at every size. */
  size?: OwnerChipSize | undefined;
  /** Drops the pill's border, fill and padding inside a host row. */
  embedded?: boolean | undefined;
  /** When set, renders the remove (×) control after the name. */
  onRemove?: (() => void) | undefined;
  /** Accessible name of the remove control (default "Remove {name}"). */
  removeLabel?: string | undefined;
}

/* `ai` is Avatar's tone paint: the soft fill, with its border colour at 1px. */
const OWNER_TONE: Record<OwnerChipTone, string> = {
  neutral: "bg-pb-card-elevated text-pb-text",
  ai: `border ${TONES.ai.soft}`,
};

const NAME_TEXT: Record<OwnerChipSize, string> = {
  md: "text-xs",
  sm: "text-dense-2",
};

const INITIALS_TEXT: Record<OwnerChipSize, string> = {
  md: "text-dense-2",
  sm: "text-dense-3",
};

const OwnerChipRoot = forwardRef<HTMLSpanElement, OwnerChipProps>(
  function OwnerChip(
    {
      initials,
      name,
      tone,
      seed,
      size = "md",
      embedded = false,
      onRemove,
      removeLabel,
      className,
      ...rest
    },
    ref,
  ) {
    const badgeTone = tone
      ? OWNER_TONE[tone]
      : `${avatarToneFor(seed ?? (typeof name === "string" ? name : typeof initials === "string" ? initials : null))} font-semibold`;
    const fit = embedded || onRemove != null;
    return (
      <span
        ref={ref}
        className={cn(
          `inline-flex items-center gap-1.5 ${SHAPE_CLASS.pill}`,
          // The remove control's 16px disc sits 4px from the end, as the
          // initials disc sits 2px inside the start.
          !embedded &&
            `ps-0.5 ${onRemove ? "pe-1" : "pe-2"} py-0.5 border border-pb-border bg-pb-card`,
          fit && "min-w-0 max-w-full [&>button]:shrink-0",
          className,
        )}
        {...rest}
      >
        <span
          className={cn(
            `inline-flex items-center justify-center w-5 h-5 ${SHAPE_CLASS.circle}`,
            INITIALS_TEXT[size],
            "font-medium",
            fit && "shrink-0",
            badgeTone,
          )}
        >
          {initials}
        </span>
        {name && (
          <span
            className={cn(
              NAME_TEXT[size],
              "text-pb-text",
              fit && "min-w-0 truncate",
            )}
          >
            {name}
          </span>
        )}
        {onRemove && (
          <ChipRemoveButton
            label={
              removeLabel ??
              (typeof name === "string" ? `Remove ${name}` : "Remove")
            }
            onRemove={onRemove}
          />
        )}
      </span>
    );
  },
);

type WithRef<P> = P & React.RefAttributes<HTMLSpanElement>;

export interface OwnerChipComponent {
  (props: WithRef<OwnerChipProps>): React.ReactNode;
  readonly $$typeof: symbol;
  displayName?: string | undefined;
}

export const OwnerChip: OwnerChipComponent = OwnerChipRoot;
